import { fmtDate, stageTitle } from '@jt/core';
import type { CSSProperties } from 'react';
import { useApplications, useStats, useToday } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Kpis, TodayBlock } from '../components/Today';
import { Empty, Logo, PageHead, SourceTag } from '../components/ui';
import { EmptyStart, HeadActions } from './Applications';

function StatsBody() {
  const ui = useUI();
  const s = useStats();
  if (!s.data) return null;
  const d = s.data;
  if (!d.total)
    return (
      <Empty
        title="Пока нечего считать"
        text="После первых откликов здесь будет процент ответов по источникам, конверсия этапов и причины отказов."
      />
    );
  return (
    <>
      <section className="box">
        <h2>Воронка</h2>
        <div className="kv" style={{ marginBottom: 12 }}>
          <div>
            Откликов<b>{d.total}</b>
          </div>
          <div>
            Ответили<b>{d.respondedPct}%</b>
          </div>
          <div>
            Дней до первого ответа<b>{d.avgFirstResponseDays ?? '—'}</b>
          </div>
        </div>
        {d.funnel.map((f) => (
          <div className="fun" key={f.stage}>
            <span>{stageTitle(f.stage)}</span>
            <span className="t">
              <span style={{ width: `${f.pct}%`, '--c': `var(--s-${f.stage})` } as CSSProperties} />
            </span>
            <span className="n">{f.count}</span>
          </div>
        ))}
      </section>
      <section className="box">
        <h2>По источникам</h2>
        <div
          className="tblw"
          style={{ border: 0 }}
          tabIndex={0}
          role="region"
          aria-label="Отклики по источникам"
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>Источник</th>
                <th className="num">Откликов</th>
                <th className="num">Ответили</th>
                <th className="num">Интервью</th>
                <th className="num">Офферы</th>
              </tr>
            </thead>
            <tbody>
              {d.bySource.map((r) => (
                <tr key={r.source} style={{ cursor: 'default' }}>
                  <td>
                    <SourceTag source={r.source} />
                  </td>
                  <td className="num">{r.count}</td>
                  <td className="num">{r.respondedPct}%</td>
                  <td className="num">{r.interviews}</td>
                  <td className="num">{r.offers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="box">
        <h2>Отказы · {d.rejections.length}</h2>
        {d.rejections.length ? (
          <div className="list">
            {d.rejections.map((r) => (
              <div
                className="lrow clickable"
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => ui.openApp(r.id)}
                onKeyDown={(e) => e.key === 'Enter' && ui.openApp(r.id)}
              >
                <Logo name={r.company} size={32} />
                <div className="lm">
                  <b>{r.company}</b> <span className="muted">{r.role}</span>
                  <div className="meta">
                    На этапе «{stageTitle(r.reached)}»{r.stageAt ? `, ${fmtDate(r.stageAt)}` : ''}
                  </div>
                  {r.reason && <div className="txt">{r.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            Отказов нет.
          </p>
        )}
      </section>
    </>
  );
}

export function OverviewPage() {
  const apps = useApplications();
  const today = useToday();
  const ai = !!today.data?.ai;
  const head = (
    <PageHead title="Обзор" sub="Вся воронка поиска работы">
      <HeadActions hasDemo={(apps.data ?? []).some((a) => a.isDemo)} ai={ai} />
    </PageHead>
  );
  if (apps.isPending) return head;
  if (!apps.data?.length)
    return (
      <>
        {head}
        <EmptyStart ai={ai} />
      </>
    );
  return (
    <>
      {head}
      <Kpis today={today.data} />
      <TodayBlock today={today.data} />
      <StatsBody />
    </>
  );
}
